import React, { useState, useEffect } from 'react';
import { Modal, Button } from '../common';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Trash2, Plus, BookOpenCheck } from 'lucide-react';

function KyTestModal({ isOpen, onClose, student }) {
  const { getAccessToken } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('stamps'); // 'stamps' or 'create'
  const [stampList, setStampList] = useState([]);
  const [treeList, setTreeList] = useState([]);
  const [selectedTreeId, setSelectedTreeId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  const fetchStampList = async () => {
    console.log('=== fetchStampList 디버깅 시작 ==='); // 디버깅용
    console.log('student 전체 객체:', JSON.stringify(student, null, 2)); // 디버깅용
    console.log('student_id 타입:', typeof student?.student_id); // 디버깅용
    console.log('student_id 값:', student?.student_id); // 디버깅용
    
    if (!student || !student.student_id) {
      console.error('No student or student_id provided:', student);
      setIsLoading(false);
      return;
    }
    
    setIsLoading(true);
    setFetchError(null); // 에러 초기화
    try {
      const token = await getAccessToken();
      console.log('Access token:', token ? 'Token exists' : 'No token'); // 디버깅용
      const url = `${process.env.REACT_APP_API_BASE_URL}/api/exams?student_id=${student.student_id}&_t=${Date.now()}`;
      console.log('Fetching stamps from:', url); // 디버깅용

      const response = await fetch(url, {
        headers: {
          'Authorization': token,
          'Cache-Control': 'no-cache' // 캐시 무시
        }
      });

      console.log('Response status:', response.status); // 디버깅용
      console.log('Response headers:', response.headers); // 디버깅용
      
      if (response.status === 204) {
        console.log('No content (204) - setting empty stamp list'); // 디버깅용
        setStampList([]);
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseText = await response.text();
      console.log('Raw response text:', responseText); // 디버깅용
      
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (e) {
        console.error('JSON parse error:', e);
        throw new Error('Invalid JSON response');
      }
      console.log('=== API 응답 디버깅 ==='); // 디버깅용
      console.log('전체 응답:', JSON.stringify(data, null, 2)); // 디버깅용
      
      // StampManagement.js와 동일한 구조로 처리
      if (data && data.data && data.data.stamps) {
        console.log('stamps 배열 길이:', data.data.stamps.length); // 디버깅용
        console.log('첫 번째 stamp:', data.data.stamps[0]); // 디버깅용
        setStampList(data.data.stamps);
      } else {
        console.error('Unexpected response structure:', data);
        setStampList([]);
      }
    } catch (error) {
      console.error('=== 스탬프 목록 조회 오류 ===');
      console.error('Error type:', error.name);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      console.error('Full error:', error);
      setFetchError(error.message);
      setStampList([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    console.log('=== KyTestModal useEffect 디버깅 ==='); // 디버깅용
    console.log('isOpen:', isOpen); // 디버깅용
    console.log('student:', student); // 디버깅용
    console.log('student?.student_id:', student?.student_id); // 디버깅용
    console.log('현재 시간:', new Date().toISOString()); // 디버깅용
    
    if (isOpen && student && student.student_id) {
      console.log('✅ Valid student data, fetching stamps...'); // 디버깅용
      setActiveTab('stamps'); // 모달 열 때 항상 목록 탭으로 초기화
      setStampList([]); // 이전 데이터 초기화
      setSelectedTreeId(null);
      setIsLoading(true); // 로딩 시작
      
      // 즉시 API 호출 (지연 제거)
      fetchStampList();
    } else if (!isOpen) {
      // 모달이 닫힐 때 상태 초기화
      console.log('🔄 Modal closed, resetting state'); // 디버깅용
      setStampList([]);
      setTreeList([]);
      setSelectedTreeId(null);
      setActiveTab('stamps');
      setIsLoading(false);
      setFetchError(null);
    }
  }, [isOpen, student?.student_id]); // student_id를 의존성에 추가

  useEffect(() => {
    if (isOpen && activeTab === 'create') {
      fetchTreeList();
    }
  }, [isOpen, activeTab]);

  const fetchTreeList = async () => {
    try {
      const token = await getAccessToken();
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/testset/lists`, {
        headers: { 'Authorization': token }
      });
      const data = await response.json();
      console.log('Tree API Response:', data); // 디버깅용
      if (data.status === 'success') {
        setTreeList(data.data || []);
      }
    } catch (error) {
      console.error('교육과정 목록 조회 실패:', error);
    }
  };

  const handleCreateStamp = async () => {
    if (!selectedTreeId || !student?.student_id) return;

    try {
      const token = await getAccessToken();
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/exams`, {
        method: 'POST',
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          tree_id: selectedTreeId,
          student_id: parseInt(student.student_id)
        })
      });

      if (response.status === 409) {
        alert('이미 존재하는 스탬프입니다.');
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      await fetchStampList();
      setActiveTab('stamps');
      setSelectedTreeId(null);
    } catch (error) {
      console.error('스탬프 생성 오류:', error);
      alert('스탬프 생성에 실패했습니다.');
    }
  };

  const handleDeleteStamp = async (stampId, examCount) => {
    if (examCount > 0) {
      alert('시험이 존재하는 스탬프는 삭제할 수 없습니다.');
      return;
    }

    if (window.confirm('스탬프를 삭제하시겠습니까?')) {
      try {
        const token = await getAccessToken();
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/exams`, {
          method: 'DELETE',
          headers: {
            'Authorization': token,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            stamp_id: stampId
          })
        });

        if (response.status === 409) {
          alert('시험이 존재하는 스탬프는 삭제할 수 없습니다.');
          return;
        }

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        await fetchStampList();
      } catch (error) {
        console.error('스탬프 삭제 오류:', error);
        alert('스탬프 삭제에 실패했습니다.');
      }
    }
  };

  const handleStampClick = (stamp) => {
    // 네비게이션 시작 시 로딩 표시
    setIsNavigating(true);
    
    // 약간의 지연 후 네비게이션 실행 (모달이 닫히는 애니메이션과 동기화)
    setTimeout(() => {
      navigate(`/student/${student.student_id}/stamp/${stamp.stamp_id}/result`);
    }, 100);
    
    onClose();
  };

  const handleWrongAnswerClick = (stamp) => {
    navigate(`/student/${student.student_id}/stamp/${stamp.stamp_id}/wronganswer`);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="교육과정 관리"
      size="lg"
      footer={
        <div className="flex w-full justify-end">
          <Button
            variant="secondary"
            onClick={onClose}
          >
            닫기
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {/* 학생카드 스타일 - ExamResult.js 디자인 적용 */}
        <div className="rounded-3xl border border-gray-200 bg-white p-[14.4px]">
          <div className="grid grid-cols-[6fr_4fr] items-center">
            {/* 좌측 영역 - 학생 정보 */}
            <div className="flex min-w-0 items-center gap-2 pr-2">
              <div 
                className="flex shrink-0 items-center justify-center rounded-full text-white font-bold text-sm bg-gradient-to-r from-primary to-primary/70"
                style={{ width: '32px', height: '32px' }}
                title={`학생 ID: ${student?.student_id || '-'}`}
              >
                {student?.student_id || '-'}
              </div>
              
              <div className="min-w-0 flex-1 overflow-hidden">
                <h4 className="truncate text-sm font-medium">{student?.student_name || '정보 없음'}</h4>
                <p className="truncate text-xs text-gray-600">
                  {student?.school_name || '정보 없음'} · {student?.school_grade ? `${student.school_grade}학년` : '학년 정보 없음'}
                </p>
              </div>
            </div>
            
            {/* 우측 영역 - 담당 선생님 & 강의 정보 */}
            <div className="flex items-center">
              <div className="flex flex-1 flex-col justify-center text-right">
                <div className="flex items-center justify-end text-xs">
                  <span className="shrink-0 text-gray-500">담당:</span>
                  <span className="ml-1 truncate font-medium text-gray-700" style={{ maxWidth: '80px' }}>
                    {student?.in_charge_teacher || '-'}
                  </span>
                </div>
                <div className="mt-0.5 flex items-center justify-end text-xs">
                  <span className="shrink-0 text-gray-500">강의:</span>
                  <span className="ml-1 truncate font-medium text-gray-700" style={{ maxWidth: '80px' }}>
                    {student?.lecture || '-'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 탭 메뉴 */}
        <div className="flex border-b">
          <button
            className={`px-4 py-2 font-medium text-sm ${
              activeTab === 'stamps' 
                ? 'text-primary border-b-2 border-primary' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('stamps')}
          >
            교육과정 목록
          </button>
          <button
            className={`px-4 py-2 font-medium text-sm ${
              activeTab === 'create' 
                ? 'text-primary border-b-2 border-primary' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('create')}
          >
            새 교육과정 추가
          </button>
        </div>

        {/* 탭 컨텐츠 */}
        {activeTab === 'stamps' ? (
          <div className="space-y-3">
            {isLoading || isNavigating ? (
              <div className="text-center py-8 text-gray-500">
                <div className="inline-flex items-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {isNavigating ? '페이지 이동 중...' : '로딩 중...'}
                </div>
                <p className="text-xs text-gray-400 mt-2">학생 ID: {student?.student_id || 'N/A'}</p>
              </div>
            ) : stampList.length > 0 ? (
              stampList.map((stamp) => (
                <div 
                  key={stamp.stamp_id} 
                  className="border border-gray-200 rounded-3xl p-4 transition-all duration-300 shadow-lg hover:shadow-2xl hover:scale-[1.02] hover:border-primary/20"
                >
                  <div className="flex justify-between items-center">
                    <div 
                      className="flex-1 cursor-pointer"
                      onClick={() => handleStampClick(stamp)}
                    >
                      <h3 className="font-semibold text-gray-900">{stamp.tree_name}</h3>
                      <p className="text-sm text-gray-600 mt-1">교육과정: {stamp.edu_ver}</p>
                      <p className="text-sm text-gray-600">테스트 횟수: {stamp.exam_count}회</p>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <Button
                        size="sm"
                        variant="primary-outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleWrongAnswerClick(stamp);
                        }}
                        className="flex items-center gap-1"
                      >
                        <BookOpenCheck className="w-4 h-4" />
                        오답노트
                      </Button>
                      {stamp.exam_count === 0 && (
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteStamp(stamp.stamp_id, stamp.exam_count);
                          }}
                          className="flex items-center gap-1"
                        >
                          <Trash2 className="w-4 h-4" />
                          삭제
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                <BookOpen className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p>등록된 교육과정이 없습니다.</p>
                <p className="text-sm mt-1">상단의 '새 교육과정 추가' 탭을 클릭하여 추가하세요.</p>
                <p className="text-xs text-gray-400 mt-2">
                  {/* 디버깅: stampList.length = {stampList.length}<br/>
                  student_id = {student?.student_id}<br/>
                  isLoading = {isLoading.toString()}<br/> */}
                  {fetchError && <span className="text-red-500">Error: {fetchError}</span>}
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                교육과정 선택
              </label>
              <select
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
                onChange={(e) => setSelectedTreeId(parseInt(e.target.value))}
                value={selectedTreeId || ''}
              >
                <option value="">교육과정을 선택하세요</option>
                {treeList
                  .filter(tree => tree.is_closed)
                  .map((tree) => (
                    <option key={tree.tree_id} value={tree.tree_id}>
                      {tree.tree_name} ({tree.edu_ver})
                    </option>
                  ))}
              </select>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => {
                  setActiveTab('stamps');
                  setSelectedTreeId(null);
                }}
              >
                취소
              </Button>
              <Button
                variant="primary"
                onClick={handleCreateStamp}
                disabled={!selectedTreeId}
                className="flex items-center gap-1"
              >
                <Plus className="w-4 h-4" />
                생성
              </Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

export default KyTestModal;